import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";

import {
	NotificationCategory,
	NotificationContentType,
	NotificationFrequency,
	NotificationPreferenceGroup,
	NotificationStatus,
	NotificationType,
} from "../enum/notification.enum";

// ── Notification ──────────────────────────────────────────────────────

export const NotificationSchema = z.object({
	id: z.number(),

	type: z.nativeEnum(NotificationType),

	contentType: z.nativeEnum(NotificationContentType),

	title: z.string().nullable(),
	message: z.string().nullable(),
	link: z.string().nullable(),

	category: z.nativeEnum(NotificationCategory).nullable(),

	isRead: z.boolean(),
	readAt: z.string().nullable(),

	metadata: z.record(z.string(), z.unknown()).nullable(),

	userId: z.number(),
	senderUserId: z.number().nullable(),

	taskId: z.number().nullable(),
	subtaskId: z.number().nullable(),
	tagId: z.number().nullable(),

	companyId: z.number().nullable(),

	status: z.nativeEnum(NotificationStatus),

	createdAt: z.string(),
	updatedAt: z.string(),
});

export type Notification = z.infer<typeof NotificationSchema>;

// ── Paginated response ────────────────────────────────────────────────

export const NotificationResponseSchema = z.object({
	data: z.array(NotificationSchema),
	meta: MetaDataSchema,
});

export type NotificationResponse = z.infer<typeof NotificationResponseSchema>;

// ── Unread count ──────────────────────────────────────────────────────

export const UnreadCountSchema = z.object({
	unreadCount: z.number(),
});

export type UnreadCount = z.infer<typeof UnreadCountSchema>;

// ── Notification Preference ───────────────────────────────────────────

export const NotificationPreferenceSchema = z.object({
	id: z.number(),

	userId: z.number(),
	companyId: z.number().nullable(),

	eventType: z.string(),

	appEnabled: z.boolean(),
	emailEnabled: z.boolean(),
	digestEnabled: z.boolean(),

	frequency: z.nativeEnum(NotificationFrequency),

	thresholds: z.record(z.string(), z.unknown()).nullable(),

	createdAt: z.string(),
	updatedAt: z.string(),
});

export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;

export const NotificationPreferencesResponseSchema = z.array(NotificationPreferenceSchema);

export type NotificationPreferencesResponse = z.infer<typeof NotificationPreferencesResponseSchema>;
// ── Upsert preference request ─────────────────────────────────────────

export const UpsertNotificationPreferenceSchema = z.object({
	groupKey: z.nativeEnum(NotificationPreferenceGroup),

	appEnabled: z.boolean().optional(),
	emailEnabled: z.boolean().optional(),
	digestEnabled: z.boolean().optional(),

	frequency: z.nativeEnum(NotificationFrequency).optional(),

	thresholds: z.record(z.string(), z.unknown()).nullable().optional(),
});

export type UpsertNotificationPreference = z.infer<typeof UpsertNotificationPreferenceSchema>;

// ── Notification group definition (from /notification-preferences/available) ──

export const NotificationGroupDefinitionSchema = z.object({
	key: z.nativeEnum(NotificationPreferenceGroup),
	label: z.string(),
	description: z.string(),
	eventTypes: z.array(z.nativeEnum(NotificationContentType)),
});

export type NotificationGroupDefinition = z.infer<typeof NotificationGroupDefinitionSchema>;

export const NotificationGroupDefinitionsResponseSchema = z.array(
	NotificationGroupDefinitionSchema
);

export type NotificationGroupDefinitionsResponse = z.infer<
	typeof NotificationGroupDefinitionsResponseSchema
>;
// ── Query params for listing notifications ────────────────────────────

// ── Query params for listing notifications ────────────────────────────

export const NotificationQueryParamsSchema = z.object({
	page: z.number().optional(),
	limit: z.number().optional(),

	isRead: z.enum(["true", "false"]).optional(),

	category: z.nativeEnum(NotificationCategory).optional(),
});

export type NotificationQueryParams = z.infer<typeof NotificationQueryParamsSchema>;
